// src/pages/TermsAndConditions.jsx

import React, { useEffect } from 'react';
import { FileText, Shield, AlertCircle, CheckCircle, Scale } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import Header from '../components/Header';
import Footer from '../components/Footer';

const TermsAndConditions = () => {
  const { language } = useLanguage();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const content = {
    en: {
      title: "Terms & Conditions",
      subtitle: "Please read these terms carefully before using our services",
      lastUpdated: "Last Updated: December 13, 2024",
      sections: [
        {
          icon: FileText,
          title: "1. Acceptance of Terms",
          content: [
            "By accessing and using the services provided by Shree Digital Solutions And Services E-Seva Kendra, you accept and agree to be bound by these Terms and Conditions.",
            "If you do not agree with any part of these terms, you must not use our services.",
            "We reserve the right to modify these terms at any time. Your continued use of our services constitutes acceptance of any changes."
          ]
        },
        {
          icon: Shield,
          title: "2. Services Provided",
          content: [
            "Shree Digital Solutions And Services E-Seva Kendra provides government documentation services, Aadhaar services, PAN card services, passport assistance, and other related services.",
            "All services are subject to availability and compliance with government regulations.",
            "We act as facilitators and intermediaries for government services. Final approval rests with respective government authorities.",
            "Service processing times may vary based on government department workloads and requirements."
          ]
        },
        {
          icon: AlertCircle,
          title: "3. User Responsibilities",
          content: [
            "You must provide accurate, complete, and up-to-date information for all service requests.",
            "You are responsible for the authenticity of all documents submitted through our services.",
            "Any misrepresentation or fraudulent information provided may result in legal action and service termination.",
            "You must maintain the confidentiality of any login credentials or account information.",
            "You agree to use our services only for lawful purposes and in accordance with applicable laws and regulations."
          ]
        },
        {
          icon: Scale,
          title: "4. Payment Terms",
          content: [
            "All fees must be paid in advance or as agreed upon before service commencement.",
            "Payment can be made through cash, UPI, or other approved payment methods.",
            "Service fees are non-refundable once processing has begun, except in cases of service failure on our part.",
            "Government fees are separate and non-refundable as they are paid directly to government departments.",
            "Any additional charges arising from changes in government fees or requirements will be communicated and must be paid before service completion."
          ]
        },
        {
          icon: FileText,
          title: "5. Service Processing & Delivery",
          content: [
            "Processing times are estimates and may vary based on government processing times and document verification requirements.",
            "We will make reasonable efforts to complete services within stated timeframes but cannot guarantee specific completion dates.",
            "Delays caused by incomplete documentation, government backlogs, or technical issues are beyond our control.",
            "You will be notified of any significant delays or issues during processing.",
            "Documents will be delivered as per the agreed method (pickup, courier, or email) upon completion."
          ]
        },
        {
          icon: Shield,
          title: "6. Privacy & Data Protection",
          content: [
            "We are committed to protecting your personal information and privacy.",
            "Your data will be used solely for the purpose of providing requested services.",
            "We implement appropriate security measures to protect your information from unauthorized access.",
            "Your information may be shared with government departments as required for service processing.",
            "For detailed information, please refer to our Privacy Policy."
          ]
        },
        {
          icon: AlertCircle,
          title: "7. Limitations of Liability",
          content: [
            "We are not liable for delays or failures in service delivery caused by government departments or technical issues beyond our control.",
            "We are not responsible for rejection of applications by government authorities due to incomplete or incorrect information provided by users.",
            "Our liability is limited to the service fees paid by you, excluding government fees.",
            "We do not guarantee approval of any government application as final decisions rest with respective authorities.",
            "We are not liable for any indirect, consequential, or incidental damages arising from use of our services."
          ]
        },
        {
          icon: CheckCircle,
          title: "8. Cancellation & Refunds",
          content: [
            "Service cancellations must be requested in writing before processing begins.",
            "Refunds will be processed within 7-10 business days for eligible cancellations.",
            "Once processing has begun, service fees are non-refundable.",
            "Government fees paid to authorities are non-refundable under any circumstances.",
            "In case of service failure on our part, you are entitled to a full refund of service fees (excluding government fees)."
          ]
        },
        {
          icon: FileText,
          title: "9. Intellectual Property",
          content: [
            "All content, logos, and materials on our website and premises are the property of Shree Digital Solutions And Services E-Seva Kendra.",
            "You may not reproduce, distribute, or use our materials without written permission.",
            "Trademarks and service marks displayed are registered to their respective owners."
          ]
        },
        {
          icon: Scale,
          title: "10. Governing Law & Jurisdiction",
          content: [
            "These terms are governed by the laws of India.",
            "Any disputes arising from these terms or use of our services shall be subject to the exclusive jurisdiction of courts in Thane, Maharashtra.",
            "We will make reasonable efforts to resolve disputes amicably before pursuing legal action."
          ]
        },
        {
          icon: AlertCircle,
          title: "11. Contact Information",
          content: [
            "For questions about these Terms & Conditions, please contact us:",
            "Email: admin@shreeservz.com",
            "Phone: +91 9321415838",
            "Address: Thane, Maharashtra, India"
          ]
        }
      ]
    },
    mr: {
      title: "अटी व शर्ती",
      subtitle: "कृपया आमच्या सेवा वापरण्यापूर्वी या अटी काळजीपूर्वक वाचा",
      lastUpdated: "शेवटचे अपडेट: डिसेंबर 13, 2024",
      sections: [
        {
          icon: FileText,
          title: "1. अटींची स्वीकृती",
          content: [
            "श्री डिजिटल सोल्यूशन्स अँड सर्व्हिसेस ई-सेवा केंद्राद्वारे प्रदान केलेल्या सेवांमध्ये प्रवेश करून आणि वापरून, तुम्ही या अटी व शर्तींशी बांधील असण्यास स्वीकारता आणि सहमत आहात.",
            "जर तुम्ही या अटींच्या कोणत्याही भागाशी सहमत नसाल, तर तुम्ही आमच्या सेवा वापरू नये.",
            "आम्ही कधीही या अटी सुधारित करण्याचा अधिकार राखून ठेवतो. आमच्या सेवांचा तुमचा सतत वापर म्हणजे कोणत्याही बदलांची स्वीकृती."
          ]
        },
        {
          icon: Shield,
          title: "2. प्रदान केलेल्या सेवा",
          content: [
            "श्री डिजिटल सोल्यूशन्स अँड सर्व्हिसेस ई-सेवा केंद्र सरकारी दस्तऐवज सेवा, आधार सेवा, पॅन कार्ड सेवा, पासपोर्ट सहाय्य आणि इतर संबंधित सेवा प्रदान करते.",
            "सर्व सेवा उपलब्धता आणि सरकारी नियमांच्या पालनाच्या अधीन आहेत.",
            "आम्ही सरकारी सेवांसाठी सुविधाकर्ते आणि मध्यस्थ म्हणून काम करतो. अंतिम मंजुरी संबंधित सरकारी अधिकाऱ्यांकडे असते.",
            "सरकारी विभागांच्या कामाच्या ओझ्यावर आधारित सेवा प्रक्रियेचा कालावधी बदलू शकतो."
          ]
        },
        {
          icon: AlertCircle,
          title: "3. वापरकर्त्याच्या जबाबदाऱ्या",
          content: [
            "सर्व सेवा विनंत्यांसाठी तुम्ही अचूक, पूर्ण आणि अद्ययावत माहिती प्रदान केली पाहिजे.",
            "आमच्या सेवांमधून सादर केलेल्या सर्व दस्तऐवजांच्या सत्यतेसाठी तुम्ही जबाबदार आहात.",
            "कोणतीही चुकीची माहिती किंवा फसवणूक केल्यास कायदेशीर कारवाई आणि सेवा बंद होऊ शकते.",
            "कोणत्याही लॉगिन क्रेडेन्शियल्स किंवा खाते माहितीची गुप्तता राखणे आवश्यक आहे.",
            "तुम्ही आमच्या सेवा केवळ कायदेशीर हेतूंसाठी आणि लागू कायदे आणि नियमांनुसार वापरण्यास सहमत आहात."
          ]
        },
        {
          icon: Scale,
          title: "4. पेमेंट अटी",
          content: [
            "सेवा सुरू होण्यापूर्वी सर्व शुल्क आगाऊ किंवा मान्यतेनुसार भरणे आवश्यक आहे.",
            "रोख, UPI किंवा इतर मान्यताप्राप्त पेमेंट पद्धतींद्वारे पेमेंट केले जाऊ शकते.",
            "प्रक्रिया सुरू झाल्यानंतर सेवा शुल्क परत केले जात नाही, आमच्या बाजूने सेवा अपयश वगळता.",
            "सरकारी शुल्क वेगळे आहे आणि परत होत नाही कारण ते थेट सरकारी विभागांना दिले जाते.",
            "सरकारी शुल्कातील बदल किंवा आवश्यकतांमुळे उद्भवणारे कोणतेही अतिरिक्त शुल्क सूचित केले जाईल आणि सेवा पूर्ण होण्यापूर्वी भरणे आवश्यक आहे."
          ]
        },
        {
          icon: FileText,
          title: "5. सेवा प्रक्रिया आणि वितरण",
          content: [
            "प्रक्रियेचा कालावधी अंदाजे आहे आणि सरकारी प्रक्रियेच्या वेळेवर आणि दस्तऐवज पडताळणीच्या आवश्यकतांवर आधारित बदलू शकतो.",
            "आम्ही सांगितलेल्या कालावधीत सेवा पूर्ण करण्यासाठी वाजवी प्रयत्न करू, परंतु विशिष्ट पूर्णता तारखांची हमी देऊ शकत नाही.",
            "अपूर्ण दस्तऐवज, सरकारी प्रलंबित प्रकरणे किंवा तांत्रिक समस्यांमुळे होणारे विलंब आमच्या नियंत्रणाबाहेर आहेत.",
            "प्रक्रियेदरम्यान कोणतेही महत्त्वपूर्ण विलंब किंवा समस्यांबद्दल तुम्हाला सूचित केले जाईल.",
            "पूर्ण झाल्यावर मान्य पद्धतीनुसार (पिकअप, कुरिअर किंवा ईमेल) दस्तऐवज वितरित केले जातील."
          ]
        },
        {
          icon: Shield,
          title: "6. गोपनीयता आणि डेटा संरक्षण",
          content: [
            "आम्ही तुमच्या वैयक्तिक माहिती आणि गोपनीयतेचे संरक्षण करण्यास वचनबद्ध आहोत.",
            "तुमचा डेटा केवळ विनंती केलेल्या सेवा प्रदान करण्याच्या उद्देशाने वापरला जाईल.",
            "आम्ही तुमच्या माहितीचे अनधिकृत प्रवेशापासून संरक्षण करण्यासाठी योग्य सुरक्षा उपाय लागू करतो.",
            "सेवा प्रक्रियेसाठी आवश्यकतेनुसार तुमची माहिती सरकारी विभागांसह सामायिक केली जाऊ शकते.",
            "तपशीलवार माहितीसाठी, कृपया आमचे गोपनीयता धोरण पहा."
          ]
        },
        {
          icon: AlertCircle,
          title: "7. दायित्वाची मर्यादा",
          content: [
            "सरकारी विभाग किंवा आमच्या नियंत्रणाबाहेरील तांत्रिक समस्यांमुळे सेवा वितरणात विलंब किंवा अपयशासाठी आम्ही जबाबदार नाही.",
            "वापरकर्त्यांनी प्रदान केलेल्या अपूर्ण किंवा चुकीच्या माहितीमुळे सरकारी अधिकाऱ्यांकडून अर्ज नाकारण्यासाठी आम्ही जबाबदार नाही.",
            "आमचे दायित्व तुम्ही भरलेल्या सेवा शुल्कापुरते मर्यादित आहे, सरकारी शुल्क वगळून.",
            "अंतिम निर्णय संबंधित अधिकाऱ्यांकडे असल्यामुळे आम्ही कोणत्याही सरकारी अर्जाच्या मंजुरीची हमी देत नाही.",
            "आमच्या सेवांच्या वापरामुळे उद्भवणाऱ्या कोणत्याही अप्रत्यक्ष, परिणामी किंवा आनुषंगिक नुकसानासाठी आम्ही जबाबदार नाही."
          ]
        },
        {
          icon: CheckCircle,
          title: "8. रद्द करणे आणि परतावा",
          content: [
            "प्रक्रिया सुरू होण्यापूर्वी लेखी स्वरूपात सेवा रद्द करण्याची विनंती करणे आवश्यक आहे.",
            "पात्र रद्दीकरणासाठी 7-10 कामकाजाच्या दिवसात परतावा प्रक्रिया केली जाईल.",
            "प्रक्रिया सुरू झाल्यानंतर, सेवा शुल्क परत होत नाही.",
            "अधिकाऱ्यांना दिलेले सरकारी शुल्क कोणत्याही परिस्थितीत परत होत नाही.",
            "आमच्या बाजूने सेवा अपयश झाल्यास, तुम्ही सेवा शुल्काच्या पूर्ण परताव्यास पात्र आहात (सरकारी शुल्क वगळून)."
          ]
        },
        {
          icon: FileText,
          title: "9. बौद्धिक मालमत्ता",
          content: [
            "आमच्या वेबसाइट आणि परिसरावरील सर्व सामग्री, लोगो आणि साहित्य श्री डिजिटल सोल्यूशन्स अँड सर्व्हिसेस ई-सेवा केंद्राची मालमत्ता आहे.",
            "लेखी परवानगीशिवाय तुम्ही आमच्या साहित्याचे पुनरुत्पादन, वितरण किंवा वापर करू शकत नाही.",
            "प्रदर्शित ट्रेडमार्क आणि सेवा चिन्हे त्यांच्या संबंधित मालकांना नोंदणीकृत आहेत."
          ]
        },
        {
          icon: Scale,
          title: "10. कायदा आणि अधिकारक्षेत्र",
          content: [
            "या अटी भारताच्या कायद्यांद्वारे नियंत्रित आहेत.",
            "या अटी किंवा आमच्या सेवांच्या वापरावरून उद्भवणारे कोणतेही विवाद ठाणे, महाराष्ट्र येथील न्यायालयांच्या विशेष अधिकारक्षेत्राच्या अधीन असतील.",
            "कायदेशीर कारवाई करण्यापूर्वी आम्ही सौहार्दपूर्णपणे विवाद सोडवण्यासाठी वाजवी प्रयत्न करू."
          ]
        },
        {
          icon: AlertCircle,
          title: "11. संपर्क माहिती",
          content: [
            "या अटी व शर्तींबद्दल प्रश्नांसाठी, कृपया आमच्याशी संपर्क साधा:",
            "ईमेल: admin@shreeservz.com",
            "फोन: +91 9321415838",
            "पत्ता: ठाणे, महाराष्ट्र, भारत"
          ]
        }
      ]
    }
  };

  const t = content[language];

  return (
    <>
      <style>{`
        .terms-page {
          min-height: 100vh;
          background: linear-gradient(135deg, #f8f9fa 0%, #fff5f2 100%);
        }
              @keyframes gradientShift {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }

        .terms-hero {
           background: linear-gradient(-45deg, rgb(201, 75, 75), rgb(75, 19, 79), rgb(30, 58, 138), rgb(6, 95, 70)) 0% 0% / 400% 400%;
        animation: 15s ease 0s infinite normal none running gradientShift;
          color: white;
          padding: 80px 20px 60px;
          text-align: center;
          position: relative;
          overflow: hidden;
        }

        .terms-hero::before {
          content: '';
          position: absolute;
          top: -50%;
          right: -10%;
          width: 500px;
          height: 500px;
          background: rgba(255, 255, 255, 0.1);
          border-radius: 50%;
        }

        .terms-hero::after {
          content: '';
          position: absolute;
          bottom: -30%;
          left: -5%;
          width: 400px;
          height: 400px;
          background: rgba(255, 255, 255, 0.08);
          border-radius: 50%;
        }

        .terms-hero-content {
          max-width: 1200px;
          margin: 0 auto;
          position: relative;
          z-index: 1;
        }

        .terms-hero h1 {
          font-size: 48px;
          font-weight: 700;
          margin-bottom: 16px;
        }

        .terms-hero p {
          font-size: 18px;
          opacity: 0.95;
          margin-bottom: 12px;
        }

        .last-updated {
          font-size: 14px;
          opacity: 0.9;
          font-weight: 500;
        }

        .terms-container {
          max-width: 1000px;
          margin: 0 auto;
          padding: 60px 20px;
        }

        .terms-section {
          background: white;
          border-radius: 16px;
          padding: 40px;
          margin-bottom: 24px;
          box-shadow: 0 4px 20px rgba(0,0,0,0.08);
          transition: transform 0.3s ease, box-shadow 0.3s ease;
        }

        .terms-section:hover {
          transform: translateY(-4px);
          box-shadow: 0 8px 30px rgba(0,0,0,0.12);
        }

        .section-header {
          display: flex;
          align-items: center;
          gap: 16px;
          margin-bottom: 24px;
        }

        .section-icon {
          width: 48px;
          height: 48px;
          background: linear-gradient(135deg, #ff6b35 0%, #ff8c42 100%);
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          flex-shrink: 0;
        }

        .section-header h2 {
          font-size: 24px;
          color: #1a1a1a;
          font-weight: 700;
          margin: 0;
        }

        .section-content p {
          font-size: 16px;
          line-height: 1.8;
          color: #4a5568;
          margin-bottom: 16px;
        }

        .section-content p:last-child {
          margin-bottom: 0;
        }

        .important-note {
          background: #fff5f2;
          border-left: 4px solid #ff6b35;
          padding: 20px;
          border-radius: 8px;
          margin-top: 40px;
        }

        .important-note h3 {
          font-size: 18px;
          color: #ff6b35;
          margin-bottom: 12px;
          font-weight: 700;
        }

        .important-note p {
          font-size: 16px;
          color: #4a5568;
          line-height: 1.6;
          margin: 0;
        }

        @media (max-width: 768px) {
          .terms-hero {
            padding: 60px 20px 40px;
          }

          .terms-hero h1 {
            font-size: 32px;
          }

          .terms-hero p {
            font-size: 16px;
          }

          .terms-container {
            padding: 40px 20px;
          }

          .terms-section {
            padding: 24px;
          }

          .section-header {
            flex-direction: column;
            align-items: flex-start;
            gap: 12px;
          }

          .section-header h2 {
            font-size: 20px;
          }

          .section-content p {
            font-size: 15px;
          }
        }
      `}</style>
    <Header language={language}/>
      <div className="terms-page">
        {/* Hero Section */}
        <div className="terms-hero">
          <div className="terms-hero-content">
            <h1>{t.title}</h1>
            <p>{t.subtitle}</p>
            <div className="last-updated">{t.lastUpdated}</div>
          </div>
        </div>

        {/* Terms Content */}
        <div className="terms-container">
          {t.sections.map((section, index) => {
            const IconComponent = section.icon;
            return (
              <div key={index} className="terms-section">
                <div className="section-header">
                  <div className="section-icon">
                    <IconComponent size={24} />
                  </div>
                  <h2>{section.title}</h2>
                </div>
                <div className="section-content">
                  {section.content.map((paragraph, pIndex) => (
                    <p key={pIndex}>{paragraph}</p>
                  ))}
                </div>
              </div>
            );
          })}

          {/* Important Note */}
          <div className="important-note">
            <h3>
              {language === 'mr' ? '⚠️ महत्त्वाची सूचना' : '⚠️ Important Note'}
            </h3>
            <p>
              {language === 'mr'
                ? 'या अटी व शर्ती वाचून आणि समजून घेतल्यानंतर आमच्या सेवा वापरणे महत्त्वाचे आहे. जर तुम्हाला कोणत्याही अटींबद्दल प्रश्न असतील तर, कृपया आमच्याशी संपर्क साधा.'
                : 'It is important to use our services after reading and understanding these Terms & Conditions. If you have any questions about any terms, please contact us.'}
            </p>
          </div>
        </div>
      </div>
      <Footer language={language}/>
    </>
  );
};

export default TermsAndConditions;